import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BadgeCheck, Sparkles, MapPin, Phone, Globe, Star } from "lucide-react";

export default function BusinessProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [claimMsg, setClaimMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [b, r] = await Promise.all([
      api.get(`/businesses/${id}`),
      api.get(`/businesses/${id}/reviews`),
    ]);
    setBiz(b.data);
    setReviews(r.data);
  };

  useEffect(() => { load(); }, [id]);

  const submitReview = async () => {
    if (!user || typeof user !== "object") { navigate("/login"); return; }
    setSubmitting(true);
    try {
      await api.post(`/businesses/${id}/reviews`, { rating, title, body });
      toast.success("Review posted");
      setReviewOpen(false); setTitle(""); setBody(""); setRating(5);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not post review");
    } finally { setSubmitting(false); }
  };

  const submitClaim = async () => {
    if (!user || typeof user !== "object") { navigate("/login"); return; }
    setSubmitting(true);
    try {
      await api.post(`/businesses/${id}/claim`, { message: claimMsg });
      toast.success("Business claimed and verified");
      setClaimOpen(false); setClaimMsg("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not claim");
    } finally { setSubmitting(false); }
  };

  if (!biz) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-500">Loading…</div>;

  const canClaim = !biz.owner_id && user && typeof user === "object" && (user.role === "business_owner" || user.role === "admin");

  return (
    <div className="pb-24" data-testid="business-profile-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-[1fr,auto] gap-8 items-start">
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {biz.featured && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-400/20 text-amber-200 border border-amber-300/40">
                  <Sparkles className="h-3 w-3" /> Premium
                </span>
              )}
              {biz.verified && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-400/20 text-green-200 border border-green-300/40" data-testid="verified-badge">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              )}
              {biz.is_professional && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-400/20 text-blue-100 border border-blue-300/40">
                  Individual Professional
                </span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="business-name">{biz.name}</h1>
            {biz.professional_title && (
              <div className="mt-2 text-amber-200 uppercase tracking-widest text-xs font-medium">{biz.professional_title}</div>
            )}
            <div className="mt-4 flex items-center gap-3 text-white/90">
              <StarRating value={biz.rating_avg} size={18} />
              <span className="font-semibold text-white">{biz.rating_avg.toFixed(1)}</span>
              <span className="text-white/70">({biz.rating_count} reviews)</span>
            </div>
            <p className="mt-6 max-w-2xl text-slate-200 leading-relaxed">{biz.description}</p>
          </div>
          <div className="flex flex-col gap-3 min-w-[220px]">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setReviewOpen(true)} data-testid="write-review-btn">
              <Star className="h-4 w-4 mr-2" /> Write a review
            </Button>
            {canClaim && (
              <Button size="lg" variant="outline" className="bg-white text-slate-900 hover:bg-slate-100 border-0" onClick={() => setClaimOpen(true)} data-testid="claim-business-btn">
                Claim this business
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Image + info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 grid md:grid-cols-[2fr,1fr] gap-8">
        {biz.image_url && (
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg bg-white">
            <img src={biz.image_url} alt={biz.name} className="w-full h-[420px] object-cover" />
          </div>
        )}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 h-fit">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500 mb-4">Contact</h3>
          <ul className="space-y-4 text-slate-700">
            <li className="flex items-start gap-3"><MapPin className="h-4 w-4 mt-1 text-blue-700" /> <span>{biz.address}<br/>{biz.city}, {biz.state}</span></li>
            {biz.phone && <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-blue-700" /> {biz.phone}</li>}
            {biz.website && /^https?:\/\//i.test(biz.website) && <li className="flex items-center gap-3 break-all"><Globe className="h-4 w-4 text-blue-700 shrink-0" /> <a href={biz.website} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">{biz.website}</a></li>}
          </ul>
        </div>
      </div>

      {/* Reviews */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">Reviews</h2>
          <Button variant="outline" onClick={() => setReviewOpen(true)} data-testid="write-review-btn-alt">Write a review</Button>
        </div>
        {reviews.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500" data-testid="reviews-empty">
            No reviews yet — be the first to share your experience.
          </div>
        ) : (
          <div className="space-y-5" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-blue-700 text-white flex items-center justify-center font-semibold text-sm">
                      {r.user_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{r.user_name}</div>
                      <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <StarRating value={r.rating} />
                </div>
                <h4 className="font-semibold text-slate-900 mt-3">{r.title}</h4>
                <p className="text-slate-600 leading-relaxed mt-1">{r.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Write a review for {biz.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Your rating</label>
              <div className="flex items-center gap-1" data-testid="review-rating-picker">
                {[1,2,3,4,5].map((i) => (
                  <button key={i} type="button" onClick={() => setRating(i)} data-testid={`review-rating-${i}`}>
                    <Star className={`h-8 w-8 ${i <= rating ? "fill-amber-500 text-amber-500" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Summarize your experience" data-testid="review-title" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Your review</label>
              <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What stood out? Would you recommend?" data-testid="review-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button onClick={submitReview} disabled={submitting || !title || !body} className="bg-blue-700 hover:bg-blue-800" data-testid="review-submit">Post review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Claim dialog */}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Claim {biz.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Claiming this listing marks it as verified, transfers ownership to your account, and unlocks the owner dashboard.
          </p>
          <Textarea value={claimMsg} onChange={(e) => setClaimMsg(e.target.value)} placeholder="Optional: tell us about your role at the business" data-testid="claim-message" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimOpen(false)}>Cancel</Button>
            <Button onClick={submitClaim} disabled={submitting} className="bg-blue-700 hover:bg-blue-800" data-testid="claim-submit">Claim & verify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
